import { useCallback, useEffect, useRef, useState } from 'react';

import { HostContext } from '../../embed-script/HostContext';
import { onPanelHidden } from '../panelVisibility';
import { RateLimitedError } from '../services/BaseService';
import { DictationService } from '../services/DictationService';
import { audioRuns, connectCapture, openMicrophone } from './microphoneCapture';
import { SPEAKING_LEVEL } from './voiceActivity';
import { floatToPcm16, levelOf, pcm16ToWav, Resampler, VOICE_SAMPLE_RATE } from './voiceAudio';

export type DictationState = 'idle' | 'starting' | 'recording' | 'transcribing';

/**
 * What the user is told, or the question would just seem lost: why a dictation left nothing in the input, or
 * (`cut_off`) that it reached the one-minute cap and lost what was said after it. `no_words` is speech the
 * transcriber made nothing of: the microphone worked, so it is not `nothing_heard`'s "check the microphone".
 */
export type DictationProblem = 'microphone' | 'audio_blocked' | 'nothing_heard' | 'no_words' | 'failed' | 'cut_off';

export interface UseDictationOptions {
    /** The chat whose input the text goes into; a dictation still running when it changes is dropped. */
    chatId: string;
    getToken: () => string | undefined;
    hostContext: HostContext | undefined;
    onText: (text: string) => void;
    onProblem: (problem: DictationProblem) => void;
}

/**
 * Quiet after speech that ends a dictation by itself. Longer than the ~2 s pauses people think in (#2578); one cut off
 * anyway loses nothing, since the next dictation is added to the text already in the input.
 */
export const DICTATION_QUIET_MS = 3000;

/** The server's cap (`VoiceDictationAudio.MaxSeconds`); a recording that reaches it is sent as it is. */
export const DICTATION_MAX_SAMPLES = 60 * VOICE_SAMPLE_RATE;

/**
 * Loud audio a recording needs, in one run, before it counts as speech: a pop as the microphone opens or a key click is
 * a few milliseconds, and alone it would start the quiet countdown and end the dictation as "nothing heard".
 */
export const DICTATION_MIN_SPEECH_SECONDS = 0.15;

/**
 * The longest quiet a run of loud audio bridges. Speech stays loud through a word; clicks and desk taps come with quiet
 * around them, so they never add up to speech over a recording, and noise is not sent for the transcriber to guess at.
 */
export const DICTATION_SPEECH_GAP_SECONDS = 0.05;

/**
 * A stop this soon with nothing heard is a click on and off, called off without a word. Later "nothing heard" is worth
 * saying: a muted or wrong microphone hears no speech, and such a recording would not end by itself.
 */
export const DICTATION_MISCLICK_MS = 2000;

/**
 * No audio at all for this long before any speech: the microphone went away (unplugged, permission revoked, a call on
 * iOS interrupting the audio). Neither the quiet countdown, which waits for speech, nor the cap, which counts samples,
 * would ever end such a recording, and the microphone would stay open.
 */
export const DICTATION_STALLED_MS = 3000;

/**
 * How long the transcription may take before the user is told it failed: past the server's own 30 s deadline plus the
 * upload. Without it a stalled request would keep both voice buttons disabled until the chat changed.
 */
export const DICTATION_UPLOAD_TIMEOUT_MS = 45_000;

interface Recording {
    release: () => void;
    startedAt: number;
    chunks: Int16Array[];
    samples: number;
    // In audio time, not the clock: blocks the main thread was too busy for arrive together.
    audioSeconds: number;
    loudUntil: number;
    loudSeconds: number;
    heardSpeech: boolean;
    lastLoudAt: number;
    lastBlockAt: number;
}

const timeout = (ms: number) => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const elapsed = new Promise<never>((_resolve, reject) => {
        timer = setTimeout(() => {
            reject(new Error('Dictation timed out'));
        }, ms);
    });
    return {
        elapsed,
        clear: () => {
            clearTimeout(timer);
        },
    };
};

/**
 * Dictation: microphone → one clip → text for the chat input. Nothing is sent while the user speaks; the clip goes to
 * the server once, when they stop or fall quiet.
 */
export const useDictation = (options: UseDictationOptions) => {
    const [state, setState] = useState<DictationState>('idle');
    const [level, setLevel] = useState(0);
    const optionsRef = useRef(options);
    optionsRef.current = options;
    // Synchronous, unlike `state`: a second click in the same tick must see the first one's start.
    const phase = useRef<DictationState>('idle');
    // Every start and every cancel takes the next number, so an await that comes back to an older one knows it was
    // called off meanwhile.
    const attempt = useRef(0);
    // Whatever the current start holds: a cancel releases the microphone at once, not when an await settles.
    const held = useRef<(() => void) | null>(null);
    const recording = useRef<Recording | null>(null);
    const upload = useRef<AbortController | null>(null);
    const levelRef = useRef(0);

    const enter = useCallback((next: DictationState) => {
        phase.current = next;
        setState(next);
        if (next !== 'recording') {
            levelRef.current = 0;
            setLevel(0);
        }
    }, []);

    const cancel = useCallback(() => {
        attempt.current += 1;
        held.current?.();
        held.current = null;
        recording.current = null;
        // Its text would be dropped; left running, the server would still transcribe it and pay for it.
        upload.current?.abort();
        upload.current = null;
        enter('idle');
    }, [enter]);

    const finish = useCallback(async () => {
        const r = recording.current;
        if (!r) {
            return;
        }
        recording.current = null;
        held.current = null;
        r.release();
        const mine = attempt.current;
        // Checked again after the await: the chat-change cancel runs in an effect, a moment after the new chat shows.
        const forChat = optionsRef.current.chatId;
        if (!r.heardSpeech) {
            enter('idle');
            optionsRef.current.onProblem('nothing_heard');
            return;
        }

        enter('transcribing');
        const samples = new Int16Array(r.samples);
        let at = 0;
        r.chunks.forEach((chunk) => {
            samples.set(chunk, at);
            at += chunk.length;
        });
        let text: string | undefined;
        let problem: DictationProblem | undefined;
        const request = new AbortController();
        upload.current = request;
        const deadline = timeout(DICTATION_UPLOAD_TIMEOUT_MS);
        try {
            const service = new DictationService(optionsRef.current.getToken() ?? '');
            const transcript = service.transcribeAsync(
                pcm16ToWav(samples),
                optionsRef.current.hostContext,
                request.signal,
            );
            text = (await Promise.race([transcript, deadline.elapsed])).trim();
            problem = text === '' ? 'no_words' : undefined;
        } catch (e) {
            // BaseService has shown its own warning for a rate limit.
            problem = e instanceof RateLimitedError ? undefined : 'failed';
        } finally {
            deadline.clear();
            // The race only stops the waiting. A user told it failed dictates again, and the upload given up on would
            // otherwise still be transcribed and paid for. The rate limit counted it on arrival either way.
            request.abort();
            if (upload.current === request) {
                upload.current = null;
            }
        }
        if (mine !== attempt.current) {
            return;
        }
        if (optionsRef.current.chatId !== forChat) {
            enter('idle');
            return;
        }
        enter('idle');
        if (text) {
            optionsRef.current.onText(text);
            // A speaker who went on, or a room never quiet enough for the auto-stop: either way the end is missing.
            if (r.samples >= DICTATION_MAX_SAMPLES) {
                optionsRef.current.onProblem('cut_off');
            }
        } else if (problem) {
            optionsRef.current.onProblem(problem);
        }
    }, [enter]);

    const start = useCallback(async () => {
        if (phase.current !== 'idle') {
            return;
        }
        const mine = ++attempt.current;
        const isCancelled = () => mine !== attempt.current;
        enter('starting');

        let context: AudioContext | undefined;
        let stream: MediaStream | undefined;
        // A cancel during the microphone prompt releases the context, and the start releases the microphone it is
        // granted after that. Track stops repeat harmlessly; a second close() would reject.
        let closed = false;
        const release = () => {
            stream?.getTracks().forEach((track) => {
                track.stop();
            });
            if (!closed) {
                closed = true;
                context?.close().catch(() => undefined);
            }
        };
        held.current = release;

        try {
            // Made in the click, before the microphone prompt: made after it, it may no longer count the click, and
            // Safari/iOS then keeps it suspended until the user clicks again. Not awaited: a resume the browser holds
            // back must not hang the start.
            context = new AudioContext();
            context.resume().catch(() => undefined);
            stream = await openMicrophone();
        } catch {
            release();
            if (!isCancelled()) {
                held.current = null;
                enter('idle');
                optionsRef.current.onProblem('microphone');
            }
            return;
        }
        if (isCancelled()) {
            release();
            return;
        }

        let node: AudioWorkletNode;
        try {
            node = (await connectCapture(context, stream)).processor;
        } catch {
            release();
            if (!isCancelled()) {
                held.current = null;
                enter('idle');
                optionsRef.current.onProblem('microphone');
            }
            return;
        }
        // Made in the click, but a browser may still hold it back: a context kept suspended records nothing, and with no
        // samples neither the auto-stop nor the cap would ever end it. A second click starts it.
        const runs = await audioRuns([context]);
        if (isCancelled()) {
            release();
            return;
        }
        if (!runs) {
            release();
            held.current = null;
            enter('idle');
            optionsRef.current.onProblem('audio_blocked');
            return;
        }

        const inputRate = context.sampleRate;
        const resampler = new Resampler(inputRate);
        const timer = setInterval(() => {
            setLevel(levelRef.current);
            const current = recording.current;
            if (!current) {
                return;
            }
            // After speech, blocks that stop arriving read as quiet too, and this sends what was said.
            const ended = current.heardSpeech
                ? Date.now() - current.lastLoudAt >= DICTATION_QUIET_MS
                : Date.now() - current.lastBlockAt >= DICTATION_STALLED_MS;
            if (ended) {
                void finish();
            }
        }, 150);
        const r: Recording = {
            release: () => {
                clearInterval(timer);
                node.port.onmessage = null;
                node.disconnect();
                release();
            },
            startedAt: Date.now(),
            chunks: [],
            samples: 0,
            audioSeconds: 0,
            loudUntil: 0,
            loudSeconds: 0,
            heardSpeech: false,
            lastLoudAt: Date.now(),
            lastBlockAt: Date.now(),
        };
        held.current = r.release;
        node.port.onmessage = (e: MessageEvent<Float32Array>) => {
            const input = e.data;
            r.lastBlockAt = Date.now();
            const seconds = input.length / inputRate;
            levelRef.current = levelOf(input);
            if (levelRef.current >= SPEAKING_LEVEL) {
                if (r.audioSeconds - r.loudUntil > DICTATION_SPEECH_GAP_SECONDS) {
                    r.loudSeconds = 0;
                }
                r.loudSeconds += seconds;
                r.loudUntil = r.audioSeconds + seconds;
                r.heardSpeech ||= r.loudSeconds >= DICTATION_MIN_SPEECH_SECONDS;
                r.lastLoudAt = Date.now();
            }
            r.audioSeconds += seconds;
            const pcm = floatToPcm16(resampler.process(input));
            const room = DICTATION_MAX_SAMPLES - r.samples;
            const kept = pcm.length <= room ? pcm : pcm.subarray(0, room);
            r.chunks.push(kept);
            r.samples += kept.length;
            if (r.samples >= DICTATION_MAX_SAMPLES) {
                void finish();
            }
        };
        recording.current = r;
        enter('recording');
    }, [enter, finish]);

    /** Sends what was said; before the microphone is up, or right after it with nothing said, calls the start off. */
    const stop = useCallback(() => {
        const r = recording.current;
        const misclick = r !== null && !r.heardSpeech && Date.now() - r.startedAt < DICTATION_MISCLICK_MS;
        if (r && !misclick) {
            void finish();
        } else if (misclick || phase.current === 'starting') {
            cancel();
        }
    }, [cancel, finish]);

    // A dictation must not land in another chat's input ("New chat" swaps the chat in place), nor keep the microphone
    // open behind a hidden panel. One already being transcribed has released the microphone, and its text may still
    // land in the hidden panel's input.
    const { chatId } = options;
    useEffect(() => cancel, [chatId, cancel]);
    useEffect(
        () =>
            onPanelHidden(() => {
                if (phase.current === 'starting' || phase.current === 'recording') {
                    cancel();
                }
            }),
        [cancel],
    );

    return { state, level, start, stop };
};
